import { useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import StudioPreviewCard from "./preview/StudioPreviewCard";
import StudioPreviewStatusPill from "./preview/StudioPreviewStatusPill";
import {
  buildStudioScenarioPayload,
  getStudioScenarioPreset,
  STUDIO_SCENARIO_PRESETS,
  type StudioScenarioPayload,
  type StudioScenarioPresetId,
} from "../../lib/studio/scenarioPresets";
import {
  appendStudioPreviewEvidence,
  createStudioScenarioEvidenceEntry,
  type StudioPreviewEvidenceEntry,
} from "../../lib/studio/studioPreviewEvidenceLedger";
import {
  runStudioVoicePreview,
  type StudioVoicePreviewExecutionResult,
  type StudioVoicePreviewLane,
} from "../../lib/studio/studioPreviewCenter";
import {
  getStudioWorkspaceVoicePersona,
  type StudioWorkspaceVoicePersonaId,
} from "../../lib/studio/studioWorkspaceState";

type ScenarioRunnerProps = {
  voiceLane: StudioVoicePreviewLane | null;
  accessBlocked?: boolean;
  initialPresetId?: StudioScenarioPresetId;
  selectedStudioPersonaId?: string | null;
  runPreview?: typeof runStudioVoicePreview;
  onEvidenceRecorded?: (entry: StudioPreviewEvidenceEntry) => void;
};

function detailTile(label: string, value: string) {
  return (
    <div
      style={{
        borderRadius: 18,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background: "#fbf8f4",
        padding: "0.9rem",
      }}
    >
      <div
        style={{
          fontSize: "0.7rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#85796e",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "0.38rem",
          color: "#26221d",
          fontWeight: 700,
          lineHeight: 1.45,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function humanizationStatusTone(status: "pass" | "warn" | "fail") {
  if (status === "pass") return "ready";
  if (status === "warn") return "warn";
  return "blocked";
}

function resultStatusTone(
  status: NonNullable<StudioVoicePreviewExecutionResult["preview"]>["status"]
) {
  if (status === "rendered") return "rendered";
  if (status === "provider_error") return "warn";
  return "blocked";
}

function previewErrorMessage(error: string | null | undefined) {
  if (!error) return "Scenario preview could not be completed.";
  if (error === "provider_not_ready") return "Preview provider is not ready.";
  if (error === "launch_critical_channel_blocked") {
    return "Live clinic channels stay blocked from the demo runner.";
  }
  if (error === "provider_request_failed") {
    return "The preview provider did not complete this request.";
  }
  return error.replace(/_/g, " ");
}

function extractPreview(
  payload:
    | StudioVoicePreviewExecutionResult
    | { error?: string; preview?: StudioVoicePreviewExecutionResult["preview"] }
    | null
    | undefined
) {
  if (!payload || typeof payload !== "object") return null;
  return "preview" in payload ? payload.preview || null : null;
}

export default function ScenarioRunner(props: ScenarioRunnerProps) {
  const executePreview = props.runPreview || runStudioVoicePreview;
  const selectedStudioPersona = props.selectedStudioPersonaId
    ? getStudioWorkspaceVoicePersona(
        props.selectedStudioPersonaId as StudioWorkspaceVoicePersonaId
      )
    : null;
  const [selectedPresetId, setSelectedPresetId] = useState<StudioScenarioPresetId>(
    props.initialPresetId || STUDIO_SCENARIO_PRESETS[0].id
  );
  const [generatedPayload, setGeneratedPayload] = useState<StudioScenarioPayload | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<StudioVoicePreviewExecutionResult["preview"] | null>(
    null
  );
  const [evidenceRecorded, setEvidenceRecorded] = useState(false);

  const selectedPreset = useMemo(
    () => getStudioScenarioPreset(selectedPresetId),
    [selectedPresetId]
  );
  const selectedChannelAllowed = Boolean(
    props.voiceLane?.allowedChannels.includes(selectedPreset.channel)
  );
  const preflightIssues = useMemo(() => {
    const issues: string[] = [];

    if (props.accessBlocked) {
      issues.push("Operator access is required for internal demo scenarios.");
    }

    if (!props.voiceLane) {
      issues.push("Voice preview lane data is unavailable.");
      return issues;
    }

    if (!props.voiceLane.ready) {
      issues.push("Voice preview lane is not ready for scenario runs.");
    }

    if (!selectedChannelAllowed) {
      issues.push(
        `Scenario channel ${selectedPreset.channel} is not currently enabled in the preview lane.`
      );
    }

    return issues;
  }, [props.accessBlocked, props.voiceLane, selectedChannelAllowed, selectedPreset.channel]);
  const canRun = Boolean(generatedPayload) && preflightIssues.length === 0 && !submitting;

  useEffect(() => {
    if (!props.initialPresetId) {
      return;
    }

    const preset = getStudioScenarioPreset(props.initialPresetId);
    setSelectedPresetId(preset.id);
    setGeneratedPayload(buildStudioScenarioPayload(preset));
    setRunError(null);
    setRunResult(null);
    setEvidenceRecorded(false);
  }, [props.initialPresetId]);

  function handleScenarioChange(event: ChangeEvent<HTMLSelectElement>) {
    setSelectedPresetId(event.target.value as StudioScenarioPresetId);
    setGeneratedPayload(null);
    setRunError(null);
    setRunResult(null);
    setEvidenceRecorded(false);
  }

  function handleGeneratePayload() {
    setGeneratedPayload(buildStudioScenarioPayload(selectedPreset));
    setRunError(null);
    setRunResult(null);
    setEvidenceRecorded(false);
  }

  async function handleRunScenario() {
    if (!generatedPayload) {
      setRunError("Generate a scenario payload before running preview.");
      return;
    }

    if (preflightIssues.length > 0) {
      setRunError(preflightIssues[0]);
      return;
    }

    setSubmitting(true);
    setRunError(null);

    try {
      const { response, payload } = await executePreview({
        channel: generatedPayload.channel,
        text: generatedPayload.text,
        correlationId: generatedPayload.correlationId,
        ...(props.selectedStudioPersonaId
          ? { studioPersonaKey: props.selectedStudioPersonaId }
          : {}),
      });
      const preview = extractPreview(payload);
      setRunResult(preview);
      setEvidenceRecorded(false);

      if (response.status === 401 || response.status === 403) {
        setRunError("Operator access is required for internal demo scenarios.");
        return;
      }

      if (preview) {
        const recordedEntry = appendStudioPreviewEvidence(
          createStudioScenarioEvidenceEntry({
            preset: selectedPreset,
            payload: generatedPayload,
            preview,
            voiceLane: props.voiceLane,
          })
        );
        if (recordedEntry) {
          setEvidenceRecorded(true);
          props.onEvidenceRecorded?.(recordedEntry);
        }
      }

      if (!response.ok) {
        setRunError(
          previewErrorMessage(
            payload && typeof payload === "object" && "error" in payload
              ? payload.error
              : null
          )
        );
        return;
      }

      setRunError(null);
    } catch {
      setRunResult(null);
      setRunError("Scenario preview could not be completed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <StudioPreviewCard title="Guided scenario runner" eyebrow="Internal-only demo flow">
      <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
        <StudioPreviewStatusPill
          status={props.voiceLane?.ready ? "ready" : "blocked"}
          label={props.voiceLane?.ready ? "lane ready" : "lane blocked"}
        />
        <StudioPreviewStatusPill
          status={selectedChannelAllowed ? "ready" : "blocked"}
          label={selectedChannelAllowed ? "scenario allowed" : "scenario blocked"}
        />
      </div>

      <p style={{ margin: "0.85rem 0 0", lineHeight: 1.65, color: "#4d453d" }}>
        Pick a deterministic demo scenario, generate the preview payload, and run it
        only inside the approved voice preview lane.
      </p>

      <div
        style={{
          marginTop: "1rem",
          display: "grid",
          gridTemplateColumns: "minmax(220px, 280px) minmax(0, 1fr)",
          gap: "0.9rem",
          alignItems: "start",
        }}
      >
        <label style={{ display: "grid", gap: "0.4rem" }}>
          <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#5a524b" }}>
            Scenario
          </span>
          <select
            aria-label="Studio scenario preset"
            value={selectedPresetId}
            onChange={handleScenarioChange}
            style={{
              borderRadius: 14,
              border: "1px solid rgba(54, 44, 35, 0.12)",
              padding: "0.72rem 0.8rem",
              font: "inherit",
              background: "#fffdfa",
            }}
          >
            {STUDIO_SCENARIO_PRESETS.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.label}
              </option>
            ))}
          </select>
        </label>

        <div
          style={{
            borderRadius: 20,
            border: "1px solid rgba(54, 44, 35, 0.08)",
            background: "rgba(251, 248, 244, 0.9)",
            padding: "0.95rem",
          }}
        >
          <div
            style={{
              fontSize: "0.75rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#7f7267",
              fontWeight: 700,
            }}
          >
            Scenario summary
          </div>
          <p style={{ margin: "0.6rem 0 0", lineHeight: 1.6, color: "#2f2924" }}>
            {selectedPreset.summary}
          </p>
          <p style={{ margin: "0.65rem 0 0", lineHeight: 1.6, color: "#5c544d" }}>
            {selectedPreset.operatorGoal}
          </p>
          <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
            <StudioPreviewStatusPill status="ready" label={selectedPreset.channel} />
          </div>
        </div>
      </div>

      <div style={{ marginTop: "0.95rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={handleGeneratePayload}
          style={{
            borderRadius: 999,
            padding: "0.78rem 1.15rem",
            border: "1px solid rgba(54, 44, 35, 0.12)",
            background: "#f5efe8",
            color: "#2d2924",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Generate scenario payload
        </button>
        <button
          type="button"
          onClick={() => void handleRunScenario()}
          disabled={!canRun}
          style={{
            borderRadius: 999,
            padding: "0.78rem 1.15rem",
            border: "1px solid rgba(41, 84, 103, 0.18)",
            background: canRun
              ? "linear-gradient(135deg, #173847, #285466)"
              : "rgba(170, 167, 162, 0.48)",
            color: canRun ? "#f7f4ee" : "#6a635c",
            fontWeight: 700,
            cursor: canRun ? (submitting ? "wait" : "pointer") : "not-allowed",
          }}
        >
          {submitting ? "Running scenario..." : "Run scenario preview"}
        </button>
      </div>

      {preflightIssues.length > 0 ? (
        <p style={{ margin: "0.85rem 0 0", color: "#8a3a3a", lineHeight: 1.6 }}>
          {preflightIssues[0]}
        </p>
      ) : null}

      {generatedPayload ? (
        <div
          style={{
            marginTop: "1rem",
            borderRadius: 22,
            border: "1px solid rgba(41, 84, 103, 0.12)",
            background:
              "linear-gradient(180deg, rgba(246, 249, 251, 0.96), rgba(255, 253, 250, 0.98))",
            padding: "1rem",
          }}
        >
          <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
            <StudioPreviewStatusPill status="rendered" label="payload generated" />
            <span style={{ color: "#48626c", fontWeight: 600 }}>
              transcript lines: {generatedPayload.transcriptLines.length}
            </span>
          </div>
          <div
            style={{
              marginTop: "0.95rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {detailTile("Scenario", generatedPayload.scenarioLabel)}
            {detailTile("Scenario execution channel", generatedPayload.channel)}
            {detailTile("Correlation ID", generatedPayload.correlationId)}
            {detailTile("Text length", String(generatedPayload.text.length))}
          </div>
          <div style={{ marginTop: "0.95rem" }}>
            <div
              style={{
                fontSize: "0.76rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#7f7267",
                fontWeight: 700,
              }}
            >
              Transcript
            </div>
            <ol
              style={{
                margin: "0.7rem 0 0",
                paddingLeft: "1.1rem",
                color: "#38484f",
                lineHeight: 1.7,
              }}
            >
              {generatedPayload.transcriptLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ol>
          </div>
        </div>
      ) : null}

      {runError ? (
        <p style={{ margin: "0.9rem 0 0", color: "#8a3a3a", fontWeight: 600 }}>
          {runError}
        </p>
      ) : null}

      {evidenceRecorded ? (
        <p style={{ margin: "0.85rem 0 0", color: "#31515d", fontWeight: 600 }}>
          Preview evidence recorded locally for Studio improve and export flows.
        </p>
      ) : null}

      {runResult ? (
        <div
          style={{
            marginTop: "1rem",
            borderRadius: 22,
            border: "1px solid rgba(54, 44, 35, 0.08)",
            background: "rgba(255, 252, 248, 0.98)",
            padding: "1rem",
          }}
        >
          <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
            <StudioPreviewStatusPill
              status={resultStatusTone(runResult.status)}
              label={runResult.status.replace(/_/g, " ")}
            />
            <StudioPreviewStatusPill
              status={humanizationStatusTone(runResult.humanization.total.status)}
              label={`humanization ${runResult.humanization.total.status}`}
            />
          </div>

          <div
            style={{
              marginTop: "0.95rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {detailTile("Request ID", runResult.requestId || "Unavailable")}
            {detailTile("Readiness", runResult.readiness.status)}
            {detailTile(
              "Humanization",
              `${runResult.humanization.total.score}/${runResult.humanization.total.maxScore}`
            )}
            {detailTile(
              "Audio type",
              runResult.audio?.contentType || "No audio returned"
            )}
            {detailTile(
              "Audio bytes",
              runResult.audio ? String(runResult.audio.bytes) : "0"
            )}
            {detailTile("Persona key", runResult.personaKey)}
            {detailTile(
              "Draft persona",
              selectedStudioPersona?.label || "Unavailable"
            )}
            {detailTile(
              "Runtime voice",
              runResult.personaSelection?.activeVoiceLabel || runResult.personaKey
            )}
            {detailTile(
              "Binding path",
              runResult.personaSelection?.bindingResolution === "studio_catalog"
                ? "Studio preview catalog"
                : "Server env default"
            )}
          </div>

          {runResult.blockedReasons?.length ? (
            <ul
              style={{
                margin: "0.95rem 0 0",
                paddingLeft: "1.1rem",
                color: "#6b5555",
                lineHeight: 1.7,
              }}
            >
              {runResult.blockedReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : null}

          <div
            style={{
              marginTop: "1rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {detailTile(
              "Repetition",
              String(runResult.humanization.dimensions.repetition)
            )}
            {detailTile(
              "Concision",
              String(runResult.humanization.dimensions.concision)
            )}
            {detailTile("Warmth", String(runResult.humanization.dimensions.warmth))}
            {detailTile(
              "Professionalism",
              String(runResult.humanization.dimensions.professionalism)
            )}
            {detailTile(
              "Close quality",
              String(runResult.humanization.dimensions.closeQuality)
            )}
            {detailTile(
              "Forbidden drift",
              String(runResult.humanization.dimensions.forbiddenDrift)
            )}
          </div>
        </div>
      ) : null}
    </StudioPreviewCard>
  );
}
